import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../api";
import {
  validateAge,
  sanitizeText,
  validateRegistrationForm,
} from "../utils/validator";
import { useLoading } from "../contexts/LoadingContext";

function UserRegister() {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    password: "",
    confirmPassword: "",
    contactNumber: "",
    gender: "",
    age: "",
    city: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const { setLoading: setGlobalLoading, setGlobalLoadingMessage } = useLoading();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);
    setGlobalLoading(true);
    setGlobalLoadingMessage("Creating your account...");

    // Sanitize and validate all inputs
    const sanitized = {
      fullName: sanitizeText(formData.fullName),
      email: sanitizeText(formData.email),
      password: sanitizeText(formData.password),
      confirmPassword: sanitizeText(formData.confirmPassword),
      contactNumber: sanitizeText(formData.contactNumber),
      gender: sanitizeText(formData.gender),
      age: formData.age,
      city: sanitizeText(formData.city),
    };

    // Validate using validator
    const validation = validateRegistrationForm(sanitized);
    if (!validation.isValid) {
      setError(Object.values(validation.errors)[0]);
      setLoading(false);
      setGlobalLoading(false);
      setGlobalLoadingMessage("");
      return;
    }

    if (!sanitizeText(formData.city).trim()) {
      setError("City is required");
      setLoading(false);
      setGlobalLoading(false);
      setGlobalLoadingMessage("");
      return;
    }

    if (!formData.gender) {
      setError("Gender is required");
      setLoading(false);
      setGlobalLoading(false);
      setGlobalLoadingMessage("");
      return;
    }

    if (!validateAge(formData.age)) {
      setError("Age must be between 18 and 100");
      setLoading(false);
      setGlobalLoading(false);
      setGlobalLoadingMessage("");
      return;
    }

    try {
      const response = await apiFetch("/public/auth/register", {
        method: "POST",
        body: JSON.stringify(sanitized),
      });

      if (response.token) {
        localStorage.setItem("publicUserToken", response.token);
        setSuccess("Registration successful! Redirecting to Find Matches...");
        setTimeout(() => navigate("/find-matches"), 1500);
      }
    } catch (err) {
      console.error("Register error:", err);
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
      setGlobalLoading(false);
      setGlobalLoadingMessage("");
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--color-background)]">
      <div className="w-full max-w-md rounded-3xl border border-[var(--color-border)] bg-[var(--color-surface)] p-8 shadow-xl shadow-[var(--color-primary-soft)]">
        <h2 className="mb-2 text-center text-2xl font-bold text-[var(--color-text-strong)]">
          Create Your Profile
        </h2>
        <p className="mb-6 text-center text-sm text-[var(--color-text-muted)]">
          Sign up to search and save profiles
        </p>

        {error && (
          <div className="mb-4 rounded-xl border border-[var(--color-danger-border)] bg-[var(--color-danger-bg)] px-4 py-2 text-[var(--color-danger-text)]">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 rounded-xl border border-[var(--color-notice-border)] bg-[var(--color-notice-bg)] px-4 py-2 text-[var(--color-notice-text)]">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Full Name */}
          <div>
            <label className="mb-2 block text-sm font-medium text-[var(--color-text-muted)]">
              Full Name
            </label>
            <input
              type="text"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              required
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text-strong)] shadow-sm focus:border-[var(--color-border-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--color-border-strong)]"
              placeholder="Your full name"
            />
          </div>

          {/* Email */}
          <div>
            <label className="mb-2 block text-sm font-medium text-[var(--color-text-muted)]">
              Email
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck="false"
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text-strong)] shadow-sm focus:border-[var(--color-border-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--color-border-strong)]"
              placeholder="your@email.com"
            />
          </div>

          {/* Contact Number */}
          <div>
            <label className="mb-2 block text-sm font-medium text-[var(--color-text-muted)]">
              Contact Number
            </label>
            <input
              type="text"
              name="contactNumber"
              value={formData.contactNumber}
              onChange={handleChange}
              required
              inputMode="numeric"
              autoCapitalize="off"
              autoCorrect="off"
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text-strong)] shadow-sm focus:border-[var(--color-border-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--color-border-strong)]"
              placeholder="+92-300-1234567"
            />
          </div>

          {/* Gender */}
          <div>
            <label className="mb-2 block text-sm font-medium text-[var(--color-text-muted)]">
              Gender
            </label>
            <select
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              required
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text-strong)] shadow-sm focus:border-[var(--color-border-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--color-border-strong)]"
            >
              <option value="">Select Gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </div>

          {/* Age */}
          <div>
            <label className="mb-2 block text-sm font-medium text-[var(--color-text-muted)]">
              Age
            </label>
            <input
              type="number"
              name="age"
              value={formData.age}
              onChange={handleChange}
              required
              min="18"
              max="100"
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text-strong)] shadow-sm focus:border-[var(--color-border-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--color-border-strong)]"
              placeholder="Your age"
            />
          </div>

          {/* City */}
          <div>
            <label className="mb-2 block text-sm font-medium text-[var(--color-text-muted)]">
              City
            </label>
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              required
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text-strong)] shadow-sm focus:border-[var(--color-border-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--color-border-strong)]"
              placeholder="Your city"
            />
          </div>

          {/* Password */}
          <div className="relative">
            <label className="mb-2 block text-sm font-medium text-[var(--color-text-muted)]">
              Password
            </label>
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck="false"
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 pr-10 text-sm text-[var(--color-text-strong)] shadow-sm focus:border-[var(--color-border-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--color-border-strong)]"
              placeholder="At least 6 characters"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-9 text-xs font-medium text-[var(--color-text)] transition hover:text-[var(--color-text-strong)]"
            >
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>

          {/* Confirm Password */}
          <div className="relative">
            <label className="mb-2 block text-sm font-medium text-[var(--color-text-muted)]">
              Confirm Password
            </label>
            <input
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck="false"
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 pr-10 text-sm text-[var(--color-text-strong)] shadow-sm focus:border-[var(--color-border-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--color-border-strong)]"
              placeholder="Re-enter your password"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-9 text-xs font-medium text-[var(--color-text)] transition hover:text-[var(--color-text-strong)]"
            >
              {showConfirmPassword ? "Hide" : "Show"}
            </button>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="mt-6 w-full rounded-lg bg-[var(--color-primary)] py-2 text-sm font-semibold text-[var(--color-on-primary)] shadow-sm transition hover:bg-[var(--color-primary-hover)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary-soft)] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Registering..." : "Register"}
          </button>
        </form>

        <p className="mt-4 text-center text-sm text-[var(--color-text)]">
          Already have an account?{" "}
          <button
            type="button"
            onClick={() => navigate("/login")}
            className="font-semibold text-[var(--color-text)] transition hover:text-[var(--color-text-strong)]"
          >
            Login
          </button>
        </p>

        <div className="mt-6 border-t border-[var(--color-border)] pt-4">
          <p className="mb-3 text-center text-xs text-[var(--color-text-muted)]">
            Are you a Marriage Bureau?
          </p>
          <button
            type="button"
            onClick={() => navigate("/bureau/register")}
            className="w-full rounded-lg border border-[var(--color-border)] py-2 text-sm font-semibold text-[var(--color-text)] transition hover:bg-[var(--color-surface-hover)]"
          >
            Register as Bureau
          </button>
        </div>
      </div>
    </div>
  );
}

export default UserRegister;
